import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum, subHeadingLevel } from "../globalProps";
import { IMAGE_SHAPE_CLASSES } from "../imageShapes";
import { SectionShell } from "../SectionShell";

const showcaseItemSchema = z.object({
  imageUrl: z.string().optional(),
  imageOriginalUrl: z.string().optional(),
  imageOriginalStorageKey: z.string().optional(),
  imageCrop: z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(), zoom: z.number(),
  }).optional(),
  title: z.string().default("Service Title"),
  body: z.string().default("Description of this service or capability."),
  ctaLabel: z.string().optional(),
  ctaHref: z.string().optional(),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("How We Help"),
  subheading: z.string().optional(),
  items: z
    .array(showcaseItemSchema)
    .default([
      {
        title: "Strategic Planning",
        body: "We work with you to develop a clear roadmap, identifying priorities and aligning resources to achieve your goals efficiently.",
        ctaLabel: "Learn More",
        ctaHref: "#",
      },
      {
        title: "Ongoing Advisory",
        body: "Regular check-ins and proactive guidance ensure you stay ahead of challenges and capitalize on opportunities as they arise.",
        ctaLabel: "Learn More",
        ctaHref: "#",
      },
      {
        title: "Document Preparation",
        body: "From contracts to compliance filings, we handle the details so you can focus on running your business.",
        ctaLabel: "Learn More",
        ctaHref: "#",
      },
    ]),
  imageShape: z.enum(["square", "rounded"]).default("rounded"),
  ctaTarget: z.enum(["_self", "_blank"]).default("_self"),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const SubH = subHeadingLevel(safe.headingLevel);

  if (safe.items.length === 0) return null;

  const imgShape = IMAGE_SHAPE_CLASSES[safe.imageShape] || IMAGE_SHAPE_CLASSES.rounded;

  return (
    <SectionShell globals={safe}>
        {/* Section header */}
        <div className="text-center mb-16">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80 max-w-2xl mx-auto"
              data-prop="subheading"
            />
          )}
        </div>

        {/* Alternating rows */}
        <div className="flex flex-col gap-16 @md:gap-20">
          {safe.items.map((item, i) => {
            const imageLeft = i % 2 === 1;

            const imageBlock = (
              <div
                className={`${imgShape} overflow-hidden`}
                data-item-prop={`items.${i}`}
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className={`aspect-[4/3] bg-surface ${imgShape}`} />
                )}
              </div>
            );

            const textBlock = (
              <div className="flex flex-col justify-center">
                <RichText
                  as={SubH}
                  html={item.title}
                  className="text-2xl @md:text-3xl font-bold mb-4"
                  data-prop={`items.${i}.title`}
                />
                <RichText
                  as="p"
                  html={item.body}
                  className="text-lg leading-relaxed opacity-80 mb-6"
                  data-prop={`items.${i}.body`}
                />
                {item.ctaLabel && item.ctaHref && (
                  <div>
                    <a
                      href={item.ctaHref}
                      target={safe.ctaTarget}
                      rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
                      className="inline-block text-primary font-semibold hover:opacity-80 transition-opacity"
                    >
                      {item.ctaLabel} &rarr;
                    </a>
                  </div>
                )}
              </div>
            );

            return (
              <div
                key={i}
                className="grid grid-cols-1 @md:grid-cols-2 gap-10 @md:gap-16 items-center"
              >
                {imageLeft ? (
                  <>
                    {imageBlock}
                    {textBlock}
                  </>
                ) : (
                  <>
                    {textBlock}
                    {imageBlock}
                  </>
                )}
              </div>
            );
          })}
        </div>
    </SectionShell>
  );
}
